import React, { useState, useEffect, useCallback } from "react";
import OfficeSelectionStep from "./OfficeSelectionStep";
import SubstituteSelectorLayout from "./SubstituteSelectorLayout";
import SubstituteSelectorContext from "./SubstituteSelectorContext";
import staffRepository from "../../repositories/staff-repository";
import "./SubstituteSelector.scss";
import OwnershipSelectionStep from "./OwnershipSelectionStep";
import StaffActionModal from "components/StaffActionModal";
import constants from "./../../constants";
import LoadingSpinner from "./LoadingSpinner";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import AllStaffMultipleAction from "./AllStaffMultipleAction";

const SubstituteSelector = ({
  staff,
  event,
  text,
  isActionExecuting,
  errorMessage,
  onClearErrorMessage,
  onCancel,
  onAction,
}) => {
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [officeSelectionIsRequired, setOfficeSelectionIsRequired] =
    useState(null);
  const [removeFromSingleOffice, setRemoveFromSingleOffice] = useState(true);
  const [isLoadingSubstitutes, setIsLoadingSubstitutes] = useState(false);
  const [substitutes, setSubstitutes] = useState([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [substituteId, setSubstituteId] = useState(null);
  const [internalErrorMessage, setInternalErrorMessage] = useState("");

  const loadSubstitutes = useCallback(
    async (removeFromSingleOfficeValue) => {
      setIsLoadingSubstitutes(true);
      setRemoveFromSingleOffice(removeFromSingleOfficeValue);

      const res = await staffRepository.getSubstitutes(
        staff.userId,
        removeFromSingleOfficeValue ? staff.officeId : null
      );

      if (res.statusCode !== 200) {
        setInternalErrorMessage(res.message);
        setIsLoadingSubstitutes(false);
        return;
      }

      setIsLoadingSubstitutes(false);
      setSubstitutes(res.data);
      setCurrentStep(2);
    },
    [staff.userId, staff.officeId]
  );

  const checkIfOfficeSelectionIsRequired = useCallback(async () => {
    const res = await staffRepository.getUserOfficesWhereStaffIsAdmin(
      staff.userId
    );

    if (res.statusCode !== 200) {
      setInternalErrorMessage(res.message);
      return;
    }

    const activeCount = res.data.filter((uo) => uo.isActive).length;
    const notDeletedCount = res.data.filter((uo) => !uo.isDeleted).length;

    if (
      (event === constants.staffActionEvents.deactivation &&
        activeCount <= 1) ||
      (event === constants.staffActionEvents.removal && notDeletedCount <= 1) ||
      (event === constants.staffActionEvents.removalAsAdmin &&
        res.data.length <= 1)
    ) {
      setOfficeSelectionIsRequired(false);
      loadSubstitutes(true);
    } else {
      setOfficeSelectionIsRequired(true);
      setCurrentStep(1);
    }
  }, [staff.userId, event, loadSubstitutes]);

  const handleBack = useCallback(() => {
    if (currentStep === 2 && officeSelectionIsRequired) {
      setCurrentStep(1);
    } else {
      onCancel();
    }
  }, [currentStep, officeSelectionIsRequired, onCancel]);

  const clearInternalErrorMessage = useCallback(() => {
    setInternalErrorMessage("");
  }, []);

  const openConfirmationModal = useCallback((substituteIdValue) => {
    setIsConfirmationModalOpen(true);
    setSubstituteId(substituteIdValue);
  }, []);

  const closeConfirmationModal = useCallback(() => {
    setIsConfirmationModalOpen(false);
  }, []);

  const handleAction = useCallback(async () => {
    setIsConfirmationModalOpen(false);
    await onAction(
      staff.userId,
      removeFromSingleOffice ? staff.officeId : null,
      substituteId
    );
  }, [
    onAction,
    removeFromSingleOffice,
    staff.officeId,
    substituteId,
    staff.userId,
  ]);

  useEffect(() => {
    if (location.search.includes("isMultiple")) return;
    checkIfOfficeSelectionIsRequired();
  }, [checkIfOfficeSelectionIsRequired, location]);

  const renderStep = () => {
    if (
      location.search.includes("isMultiple") ||
      [
        constants.staffActionEvents.multipleDeactivationConfirm,
        constants.staffActionEvents.multipleRemoveConfirm,
        constants.staffActionEvents.multipleActivationConfirm,
        constants.staffActionEvents.multipleCancelConfirm,
      ].includes(event)
    ) {
      return <AllStaffMultipleAction resetApi={onCancel} />;
    }
    if (currentStep === 0) {
      return <LoadingSpinner />;
    }
    if (currentStep === 1) {
      return (
        <OfficeSelectionStep
          isLoadingSubstitutes={isLoadingSubstitutes}
          removeFromSingleOffice={removeFromSingleOffice}
          updateStep={loadSubstitutes}
        />
      );
    }

    if (currentStep === 2) {
      return (
        <OwnershipSelectionStep
          removeFromSingleOffice={removeFromSingleOffice}
          substitutes={substitutes}
          onConfirm={openConfirmationModal}
        />
      );
    }

    return null;
  };

  return (
    <SubstituteSelectorContext.Provider
      value={{
        staff,
        event,
        text,
        isActionExecuting,
        onBack: handleBack,
        onCancel,
      }}
    >
      <SubstituteSelectorLayout
        errorMessage={errorMessage}
        onClearErrorMessage={onClearErrorMessage}
        internalErrorMessage={internalErrorMessage}
        onClearInternalErrorMessage={clearInternalErrorMessage}
      >
        {renderStep()}
      </SubstituteSelectorLayout>
      <StaffActionModal
        event={event}
        isOpen={isConfirmationModalOpen}
        onAction={handleAction}
        onClose={closeConfirmationModal}
      />
    </SubstituteSelectorContext.Provider>
  );
};

export default SubstituteSelector;
